import Color from "color";
import "aws-amplify/auth/enable-oauth-listener";
import React, {ReactElement, useEffect, useState} from "react";
import {useRouter} from "next/router";
import {
    App, EDispatchActions, SignInButton, useAppService,
} from "../app";
import {SingleViewLayout, SystemMessage} from "../components/layout";
import {getLogger, getPublicRuntimeConfig} from "../util";
import {isAuthBusy as isAuthBusyFcn} from "../app/context/isAuthBusy";
import {useCognito} from "../api/hooks/useCognito";
import {MajorButton, MinorButton} from "../components/button";
import {switchToCw4IfNeeded, switchToCw4} from "../util/handleCw4";
import {ALT_SITE_CONFIG_KEY} from "../app/context/storage";
import {isCordovaSsoState} from "../app/service/cordovaAuthState";
import {AppDeeplinkError} from "../app/error/AppDeeplinkError";

const publicRuntimeConfig = getPublicRuntimeConfig();

/**
 * An entry page that deeply routes the user to the destination
 *
 * If the user is unauthenticated then show the sign-in page.
 *
 * If the user is authenticated we want to redirect somewhere:
 *
 * 1. if a deep link was provided, follow it
 * 2. if no deep link was provided, go to the dashboard (web vs mobile are different)
 *
 * While signing in, there's a bit of redirecting going on to Cognito's hosted UI, so
 * there is handling of this period of time via _isSigningIn_ and _isAuthBusy_ variables.
 *
 * For iOS, there's a problem where the redirect doesn't work in at Cognito's Auth.js due
 * to it trying to redirect from an app:// to an https://. This is not a problem on Android,
 * but for iOS it causes the component to hang on the 'Please Wait...' message. To solve this,
 * this script also handles a timeout condition to attempt a manual redirect.
 *
 */
const IndexPage = (): ReactElement => {
    const logger = getLogger("index");
    const router = useRouter();
    const appService = useAppService();
    const [retries, setRetries] = useState(0);
    const [hasSigninFailed, setHasSigninFailed] = useState(false);
    const {appInstance} = appService.appState;
    const versionNumber = publicRuntimeConfig.versionNumber;
    const buildNumber = publicRuntimeConfig.buildNumber;
    const includeCw4 = publicRuntimeConfig?.cordova?.includeCw4 ?? false;
    let query = Object.fromEntries(new URLSearchParams(window.location.search));
    let isSigningIn = ("code" in query && "state" in query);
    let isAuthBusy = isAuthBusyFcn(appService.appState);
    const isDevelopment = publicRuntimeConfig.isDevelopment ?? false;
    let pathname = window.location.pathname;
    const paramsArr = pathname?.replace(/^\/|\/$/g, "")?.split("/");
    // Redirect to public page if it's a public url
    const isPublicUrl = (paramsArr?.length >= 2) && (paramsArr[1] === "public");

    // Handles platform switching
    const maxTapsToSwitch = 13;
    const [switchPlatformTaps, incrementSwitchPlatformTaps] = useState(0);

    // Checks if it's the Cognito SSO redirect url
    const [authCallback] = useState(() => {
        const params = new URLSearchParams(window.location.search);

        return {
            hasResult: params.has("code") || params.has("error"),
            state: params.get("state"),
        };
    });

    // Indicates we are redirected from the Cognito SSO url but the app deep linking failed and the user is stuck on the web layout
    const appDeepLinkFailed = (
        !publicRuntimeConfig.isCordova
        && authCallback.hasResult
        && isCordovaSsoState(authCallback.state)
    );

    const isHomeUrl = (pathname === "/");
    const siteId = appService.getCurrentSite();

    // A user can switch platform by tapping the version string 13 times
    const handleSwitchAlternatePlatformTap = (): void => {
        if (!publicRuntimeConfig?.alternateSite) { // !cordovaService.isCordovaAvailable ||
            logger.warn("User is tapping version but the alternate platform is not available");
            return;
        }
        if (switchPlatformTaps >= maxTapsToSwitch) {
            handleSwitchAlternatePlatformConfig();
        } else {
            incrementSwitchPlatformTaps(switchPlatformTaps + 1);
        }
    };

    // When the user gets to 13, we switch the site by setting a local storage key and reloading the page.
    const handleSwitchAlternatePlatformConfig = (): void => {
        const currentAlternateSiteConfig = Boolean(Number(window.localStorage.getItem(ALT_SITE_CONFIG_KEY)));
        window.localStorage.setItem(ALT_SITE_CONFIG_KEY, String(Number(!currentAlternateSiteConfig)));
        setTimeout(() => {
            window.location.reload();
        }, 100);
    };

    // note: do not route to /user/signin from this page, as that redirects back here and it will cause a redirect loop
    const handleRedirection = (): void => {
        if (isHomeUrl || isSigningIn) {
            // if going home, or _still_ signing in after the timeout, then go home
            if (appService.appState.settings.layout === "web") {
                // web dashboard
                const homeUrl = appService.getHomeUrl();
                logger.debug(`Routing to web home: ${homeUrl}`);
                router.replace(homeUrl);
            } else if (!appService.getCurrentSite()) {
                router.replace("/sites");
            } else if (appService.getCurrentEvent()) {
                // mobile dashboard, with event
                if (router.pathname !== "/app") {
                    logger.debug("Routing to app dashboard");
                    router.replace("/app");
                }
            } else {
                // mobile dashboard, no event
                logger.debug("Routing to app event switcher");
                router.replace("/app/duty");
            }
        } else {
            // This provides deep routing into the app from the index page, which
            // is the one hit when doing our static-hosted version of server-side routing.
            if (pathname === "/r" && "id" in query) {
                pathname = `/${siteId}/r`;
            }
            // remove the trailing slash if present on any long routes (other than on the top-level '/' route)
            if (pathname.length > 1 && pathname.endsWith("/")) {
                pathname = pathname.slice(0, pathname.length - 1);
            }
            logger.log(`Routing to ${window.location.pathname} with query ${JSON.stringify(query)}`);
            router.replace(
                {
                    pathname,
                    query,
                },
                undefined,
                {
                    shallow: true,
                }
            );
        }
    };

    useCognito();

    useEffect(() => {
        if (appDeepLinkFailed) {
            return undefined;
        }
        if (includeCw4) {
            // Handle cw4 redirections if a user is logged in and the app restarts
            switchToCw4IfNeeded();
        }
        // This is repeated because it can change in the useEffect loop
        query = Object.fromEntries(new URLSearchParams(window.location.search));
        isSigningIn = ("code" in query && "state" in query && Object.keys(query).length === 2);
        isAuthBusy = isAuthBusyFcn(appService.appState);
        // The static-hosted 404 fallback lands unauthenticated deep links to /user/signin here.
        // externalSsoProviderId needs to reach the real signin page even when unauthenticated,
        // so it's carved out from the "no auth, stay put" branch below.
        const hasExternalSsoProviderIdDeepLink = ("externalSsoProviderId" in query) && !isHomeUrl;
        if (isPublicUrl || hasExternalSsoProviderIdDeepLink) {
            handleRedirection();
        } else if (appService.appState.auth) {
            if (!isSigningIn) {
                logger.debug("Got auth and not signing in so redirecting");
                handleRedirection();
            } else if (!isAuthBusy) {
                logger.warn("Got auth and still signing in, but it timed out so redirecting");
                handleRedirection();
            } else {
                // check again in 100ms
                logger.debug("Got auth, still busy so doing nothing");
                const timeoutHandle = setTimeout(() => {
                    logger.debug("updating stats for another retry");
                    setRetries(retries + 1);
                }, 100);
                return () => {
                    clearTimeout(timeoutHandle);
                };
            }
        } else if (isSigningIn) {
            logger.debug("Got to / while signing in");
            // after 5 seconds, the login session hasn't worked, so show a message
            const timeoutHandle = setTimeout(() => {
                setHasSigninFailed(true);
            }, 10_000);
            return () => {
                clearTimeout(timeoutHandle);
            };
        } else {
            logger.debug("Got to / but no auth so staying on this page");
        }
        return undefined;
    }, [appDeepLinkFailed, appService.appState, isAuthBusy, retries]);

    // determine the sign-in button colour
    const defaultThemeBackground = publicRuntimeConfig.defaultThemeBackground ?? "#1c548c";
    const buttonStyle = {
        borderColor: Color(defaultThemeBackground).darken(0.3).hex(),
        backgroundColor: Color(defaultThemeBackground).lighten(0.6).hex(),
        width: "300px",
    };
    const cw4SignInStyle = {
        borderColor: Color(defaultThemeBackground).darken(0.3).hex(),
        backgroundColor: "white",
        width: "300px",
    };

    // determine layout
    if ("layout" in query) {
        const requestedLayout = query.layout;
        if (["app-single", "app-split", "app", "web"].includes(requestedLayout) && appService.appState.settings.layout !== requestedLayout) {
            appService.appDispatch({
                type: EDispatchActions.settingsSet,
                value: {
                    layout: requestedLayout,
                },
            });
        }
    }

    // Handle "Redirecting..." without ever redirecting. This happens 1-in-100 times, and the solution
    // is to wait a while and refresh the page. This is a workaround for a likely bug in the AWS Amplify library.
    useEffect(() => {
        let handle = null;
        if (!isSigningIn && !hasSigninFailed && appService.getCurrentAuth()) {
            handle = setTimeout(() => {
                window.location.reload();
            }, 5_000);
        }
        return (): void => {
            if (handle) {
                clearTimeout(handle);
            }
        };
    }, [isSigningIn, hasSigninFailed, appService]);

    if (appDeepLinkFailed) {
        return <AppDeeplinkError />;
    }

    const version = `Version ${versionNumber} (${buildNumber}${isDevelopment ? "-DEV" : ""})`;
    // show a temporary page until useEffect() kicks in and redirects us
    return (
        <App>
            {/* Forcing single view mobile app layout here because it looks better, even on web */}
            <SingleViewLayout showSimpleLayout={true}>
                <div className="view view-standalone centered reversed" style={{backgroundColor: defaultThemeBackground}}>
                    {(isSigningIn && !hasSigninFailed) && (
                        <>
                            {(isAuthBusy) ? (
                                <>
                                    <h1>Checking your credentials...</h1>
                                </>
                            ) : (
                                <>
                                    <h1>Signing you in...</h1>
                                </>
                            )}
                        </>
                    )}
                    {(hasSigninFailed) && (
                        <div className="wrap" style={{position: "unset", height: "50%"}}>
                            <h1>Sign-in failed</h1>
                            <SystemMessage type="warning">
                                The system is currently unavailable. Please try again later.
                            </SystemMessage>
                            <p><MajorButton href="/user/signout">Try Again</MajorButton></p>
                        </div>
                    )}
                    {(!isSigningIn && !hasSigninFailed) && (
                        <>
                            {(!appService.getCurrentAuth()) ? (
                                <div className="wrap" style={{position: "unset", height: "50%"}}>
                                    <img className="logo" src={appInstance.logoFile} alt="Logo" />
                                    <h1 style={{color: "white"}}>Welcome to {appInstance.siteName}</h1>
                                    {(!appService.isOnline) && (
                                        <div className="chl2_warning">
                                            {appService.networkDetectorContext.offlineReason ?? "The service is not available right now."}
                                        </div>
                                    )}
                                    {includeCw4 ? (
                                        <p>
                                            <p>
                                                Sign in using the method that matches your system&apos;s version.
                                                Check with your administrator if unsure.
                                            </p>
                                            <p><SignInButton label="Sign Into Crisisworks 5" style={buttonStyle} /></p>
                                            <p>
                                                <MinorButton
                                                    onClick={switchToCw4}
                                                    label="Sign Into Crisisworks 4"
                                                    style={cw4SignInStyle}
                                                />
                                            </p>
                                        </p>
                                    ) : (
                                        <>
                                            <p><SignInButton style={buttonStyle} /></p>
                                            <p>
                                                Don&apos;t share your access credentials with anyone else.
                                                If you are using a shared device, sign out when you&apos;ve
                                                finished working to stay secure. Your organisation&apos;s information
                                                security policies apply while using this service.
                                            </p>
                                        </>
                                    )}
                                    <p onClick={handleSwitchAlternatePlatformTap} style={publicRuntimeConfig?.alternateSite && {cursor: "pointer"}}>{switchPlatformTaps < 5 ? `App ${version}` : `${maxTapsToSwitch - switchPlatformTaps} more to go`}</p>
                                </div>
                            ) : (
                                <>
                                    {/* This reloads the document in 5 seconds if stuck here; see useEffect() above */}
                                    <h1>Redirecting...</h1>
                                </>
                            )}
                        </>
                    )}

                </div>
            </SingleViewLayout>
        </App>
    );
};

export default IndexPage;
